/**
 * Slice 3 — OutreachPanel (multi-channel editable draft generation).
 *
 * Architecture:
 *   - One panel, four channel tabs (email · IG DM · LinkedIn · contact form)
 *   - Generate (deterministic) → Edit inline → Save / Copy / Polish / Regenerate
 *   - Evidence preview ABOVE Generate so the user sees what findings will
 *     drive the outreach BEFORE consuming any compute.
 *   - Email also gets an "Email preview" sub-tab — minimal premium SaaS
 *     style (Stripe / Notion / Linear vibe). Same content, rendered as
 *     a plain HTML mock of how it'll arrive in the inbox.
 *
 * Profile defaults (sender / company / signature / voice) are pulled
 * from GET /api/lead-intel/outreach/defaults once on mount; per-draft
 * overrides live in local state only.
 */
import axios from "axios";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, Instagram, Linkedin, MessageSquare, Eye, Code as CodeIcon, Send } from "lucide-react";

import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const tokenHeader = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

const CHANNELS = [
  { id: "email",        label: "Email",       icon: Mail },
  { id: "ig_dm",        label: "Instagram",   icon: Instagram },
  { id: "linkedin",     label: "LinkedIn",    icon: Linkedin },
  { id: "contact_form", label: "Contact",     icon: MessageSquare },
];

// ─────────────────────────────────────────────────────────────────────────


export default function OutreachPanel({ leadId, leadName, lead, onLeadUpdate }) {
  const [activeChannel, setActiveChannel] = useState("email");
  const [emailView, setEmailView] = useState("text");  // "text" | "preview"

  // Profile defaults (set once on mount)
  const [defaults, setDefaults] = useState({
    sender_name: "", company_name: "", signature: "", voice: "warm, professional, jargon-free",
  });
  const [defaultsSaving, setDefaultsSaving] = useState(false);

  // Per-channel draft state. Keyed by channel id.
  const [drafts, setDrafts] = useState({
    email: null, ig_dm: null, linkedin: null, contact_form: null,
  });
  const [generating, setGenerating] = useState(false);
  const [polishing, setPolishing] = useState(false);
  const [saving, setSaving] = useState(false);

  // Slice 3.4 — recipient + send state
  // candidateEmails is the union of lead.email + lead.discovered_emails so
  // the user can pick a different decision-maker if a better contact was
  // discovered during crawl. toEmail is the editable per-session selection.
  const [candidateEmails, setCandidateEmails] = useState([]);
  const [toEmail, setToEmail] = useState("");
  const [sendStatus, setSendStatus] = useState(null);  // {sent_today, cap, suppressed: {email: {...}}}
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sending, setSending] = useState(false);

  // Manual sender picker — workspace-level pool from Settings → Business Profile.
  // Empty string "" means "use the workspace default From (leads@heyintake.com)".
  const [fromSenderId, setFromSenderId] = useState("");

  // Per-draft inputs (overrides for the next Generate)
  const [ctaId, setCtaId] = useState("quick_chat");
  const [ctaLibrary, setCtaLibrary] = useState([]);

  // Slice 3.1 — email templates from the canonical templates system
  // (category=lead_intel_outreach). When a template is selected, the
  // email Generate path will render from that template instead of the
  // deterministic builder. Selection is per-session only.
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [emailTemplateId, setEmailTemplateId] = useState("");  // "" = no template

  // Evidence preview (shown above generate buttons)
  const [evidence, setEvidence] = useState(null);

  // Persisted drafts loaded from the server
  const [savedDrafts, setSavedDrafts] = useState([]);

  // ─── Bootstrap: defaults, CTA library, evidence preview, saved drafts ──
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [defR, ctaR, evR, draftsR, tmplR, statusR] = await Promise.all([
          axios.get(`${API}/lead-intel/outreach/defaults`, { headers: tokenHeader() }),
          axios.get(`${API}/lead-intel/outreach/cta-library`, { headers: tokenHeader() }),
          axios.get(`${API}/lead-intel/leads/${leadId}/outreach/evidence-preview`, { headers: tokenHeader() }),
          axios.get(`${API}/lead-intel/leads/${leadId}/outreach/drafts`, { headers: tokenHeader() }),
          axios.get(`${API}/workflow/templates?category=lead_intel_outreach`, { headers: tokenHeader() }),
          axios.get(`${API}/lead-intel/leads/${leadId}/outreach/send-status`, { headers: tokenHeader() }),
        ]);
        if (!alive) return;
        setDefaults(defR.data);
        setCtaLibrary(ctaR.data?.ctas || []);
        setEvidence(evR.data);
        setSavedDrafts(draftsR.data?.drafts || []);
        setEmailTemplates(tmplR.data?.templates || []);
        const cands = statusR.data?.candidate_emails || [];
        setCandidateEmails(cands);
        if (cands.length > 0) setToEmail(cands[0]);
        setSendStatus(statusR.data);
        // Default the sender picker to the first active alternate sender if
        // any are configured. "" keeps the workspace-default From.
        const pool = statusR.data?.senders || [];
        if (pool.length > 0) setFromSenderId(pool[0].id);
      } catch (e) { /* silent — panel still renders */ }
    })();
    return () => { alive = false; };
  }, [leadId]);

  const saveDefaults = async () => {
    setDefaultsSaving(true);
    try {
      await axios.put(`${API}/lead-intel/outreach/defaults`, {
        sender_name: defaults.sender_name,
        company_name: defaults.company_name,
        signature: defaults.signature,
        voice: defaults.voice,
      }, { headers: tokenHeader() });
      toast.success("Defaults saved");
    } catch (e) {
      toast.error("Couldn't save defaults");
    } finally { setDefaultsSaving(false); }
  };

  const generate = async () => {
    setGenerating(true);
    try {
      const r = await axios.post(`${API}/lead-intel/leads/${leadId}/outreach/generate`, {
        channel: activeChannel,
        cta_id: ctaId,
        sender_name: defaults.sender_name,
        company_name: defaults.company_name,
        signature: defaults.signature,
        save: false,
        template_id: activeChannel === "email" && emailTemplateId ? emailTemplateId : undefined,
      }, { headers: tokenHeader() });
      setDrafts(d => ({ ...d, [activeChannel]: { ...r.data, dirty: false, polished: false } }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generate failed");
    } finally { setGenerating(false); }
  };

  // Slice 3.4 — Send. Triggered after the confirmation modal closes with
  // confirm=true. Reads the current email draft AS-IS so the user sends
  // exactly what they see in the editor.
  const sendEmail = async () => {
    const d = drafts.email;
    if (!d || !toEmail) return;
    setSending(true);
    try {
      const r = await axios.post(
        `${API}/lead-intel/leads/${leadId}/outreach/send`,
        {
          to_email: toEmail,
          subject: d.subject || "",
          body: d.body || "",
          is_html: (d.body || "").toLowerCase().includes("<html") || (d.body || "").toLowerCase().includes("<table"),
          template_id: d.template_id || null,
          from_sender_id: fromSenderId || null,
          confirm: true,
        },
        { headers: tokenHeader() },
      );
      toast.success(`Sent to ${r.data.to_email}`);
      setConfirmOpen(false);
      // Refresh status counters + propagate phase flip
      setSendStatus(s => s ? { ...s, sent_today: r.data.sent_today, remaining: Math.max(0, r.data.cap - r.data.sent_today) } : s);
      if (onLeadUpdate) onLeadUpdate({ lead_phase: r.data.lead_phase, last_contacted_at: r.data.sent_at });
    } catch (e) {
      const data = e?.response?.data;
      const status = e?.response?.status;
      if (status === 409 && data?.status === "blocked_suppressed") {
        toast.error(`Recipient unsubscribed — ${data.detail}`);
        setSendStatus(s => s ? { ...s, suppressed: { ...(s.suppressed || {}), [toEmail]: { ...data } } } : s);
      } else if (status === 429) {
        toast.error(data?.detail || "Daily send cap reached");
      } else if (status === 422 && data?.code === "DOMAIN_NOT_VERIFIED") {
        // Operator-fixable: their sender domain isn't verified in Resend.
        // Keep the toast open longer with the actionable detail + link.
        toast.error(data.detail, {
          duration: 15000,
          action: {
            label: "Open Resend",
            onClick: () => window.open("https://resend.com/domains", "_blank", "noreferrer"),
          },
        });
      } else if (status === 422 && data?.code === "API_KEY_NOT_AUTHORIZED") {
        // Operator-fixable: domain IS verified but the API key is scoped to
        // a different domain. Fix is to rotate to a Full-Access key.
        toast.error(data.detail, {
          duration: 20000,
          action: {
            label: "Open Resend keys",
            onClick: () => window.open("https://resend.com/api-keys", "_blank", "noreferrer"),
          },
        });
      } else {
        toast.error(data?.detail || "Send failed — please retry");
      }
    } finally { setSending(false); }
  };

  const isSuppressedRecipient = sendStatus && toEmail && sendStatus.suppressed && sendStatus.suppressed[toEmail];
  const canSendEmail = !!(
    activeChannel === "email"
    && drafts.email
    && toEmail
    && /@/.test(toEmail)
    && !isSuppressedRecipient
    && (sendStatus ? sendStatus.remaining > 0 : true)
  );

  const polish = async () => {
    const d = drafts[activeChannel];
    if (!d) { toast.info("Generate a draft first"); return; }
    setPolishing(true);
    try {
      const r = await axios.post(`${API}/lead-intel/leads/${leadId}/outreach/polish`, {
        channel: activeChannel,
        voice: defaults.voice,
        subject: d.subject,
        body: d.body,
        signature: d.signature,
      }, { headers: tokenHeader() });
      if (r.data.polished) {
        setDrafts(prev => ({
          ...prev,
          [activeChannel]: {
            ...prev[activeChannel],
            subject: r.data.subject,
            body: r.data.body,
            signature: r.data.signature,
            polished: true,
            dirty: true,
          }
        }));
        toast.success(`Polished with "${r.data.voice}"`);
      } else {
        toast.warning(r.data.polish_error || "Polish unavailable");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Polish failed");
    } finally { setPolishing(false); }
  };

  const save = async () => {
    const d = drafts[activeChannel];
    if (!d) return;
    setSaving(true);
    try {
      // If draft has no ID yet, persist via generate(save=true) — easier
      // path because the server fills in evidence_refs etc.
      if (!d.id) {
        const r = await axios.post(`${API}/lead-intel/leads/${leadId}/outreach/generate`, {
          channel: activeChannel,
          cta_id: ctaId,
          sender_name: defaults.sender_name,
          company_name: defaults.company_name,
          signature: defaults.signature,
          save: true,
          template_id: activeChannel === "email" && emailTemplateId ? emailTemplateId : undefined,
        }, { headers: tokenHeader() });
        // Then overwrite with the user's edited text
        await axios.put(`${API}/lead-intel/leads/${leadId}/outreach/drafts/${r.data.id}`, {
          subject: d.subject,
          body: d.body,
          signature: d.signature,
        }, { headers: tokenHeader() });
        setDrafts(prev => ({ ...prev, [activeChannel]: { ...prev[activeChannel], id: r.data.id, dirty: false } }));
      } else {
        await axios.put(`${API}/lead-intel/leads/${leadId}/outreach/drafts/${d.id}`, {
          subject: d.subject,
          body: d.body,
          signature: d.signature,
        }, { headers: tokenHeader() });
        setDrafts(prev => ({ ...prev, [activeChannel]: { ...prev[activeChannel], dirty: false } }));
      }
      const drR = await axios.get(`${API}/lead-intel/leads/${leadId}/outreach/drafts`, { headers: tokenHeader() });
      setSavedDrafts(drR.data?.drafts || []);
      toast.success("Draft saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const copy = async () => {
    const d = drafts[activeChannel]; if (!d) return;
    const txt = [d.subject ? `Subject: ${d.subject}` : null, "", d.body, d.signature ? `\n${d.signature}` : ""]
      .filter(x => x !== null).join("\n");
    try {
      await navigator.clipboard.writeText(txt.trim());
      toast.success("Copied to clipboard");
    } catch { toast.error("Couldn't copy"); }
  };

  const onFieldChange = (field, val) => {
    setDrafts(prev => ({
      ...prev,
      [activeChannel]: { ...(prev[activeChannel] || {}), [field]: val, dirty: true }
    }));
  };

  const draft = drafts[activeChannel];
  const channel = CHANNELS.find(c => c.id === activeChannel);

  return (
    <Card className="rounded-xl" data-testid="outreach-panel">
      <CardContent className="p-3 space-y-3">
        {/* Header */}
        <div className="flex items-baseline justify-between">
          <div className="text-xs font-medium text-zinc-700 uppercase tracking-wider">Outreach</div>
          <div className="text-[10px] text-zinc-400">
            Drafts only — review &amp; copy. Manually move to Contacted when sent.
          </div>
        </div>

        {/* Channel tabs */}
        <div className="flex gap-1 border-b border-zinc-100" data-testid="outreach-channel-tabs">
          {CHANNELS.map(c => {
            const Icon = c.icon;
            const active = activeChannel === c.id;
            const hasDraft = !!drafts[c.id];
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => setActiveChannel(c.id)}
                data-testid={`outreach-tab-${c.id}`}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs border-b-2 transition-colors ${
                  active
                    ? "border-zinc-900 text-zinc-900 font-semibold"
                    : "border-transparent text-zinc-500 hover:text-zinc-700"
                }`}
              >
                <Icon className="w-3.5 h-3.5" /> {c.label}
                {hasDraft && <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"/>}
              </button>
            );
          })}
        </div>

        {/* Evidence preview — shown before generation */}
        {evidence && (
          <details className="text-[11px] bg-zinc-50 rounded-md border border-zinc-100 px-2 py-1.5" data-testid="evidence-preview">
            <summary className="cursor-pointer text-zinc-700 font-medium">
              Evidence used <span className="text-zinc-400">({(evidence.usable_findings || []).length} findings · +{evidence.total_recoverable_points} pts recoverable)</span>
            </summary>
            <ul className="mt-1.5 space-y-0.5 pl-3">
              {evidence.usable_findings.map((f, i) => (
                <li key={`ev-${i}`} className="text-zinc-700">
                  <span className="font-mono tabular-nums text-amber-700 font-semibold">+{f.points_lost}</span>{" "}
                  <span className="font-medium">{f.category}</span>
                  <span className="text-zinc-400"> — {f.evidence}</span>
                </li>
              ))}
            </ul>
            {evidence.internal_only_findings?.length > 0 && (
              <div className="text-[10px] text-zinc-400 mt-1 pl-3 italic">
                {evidence.internal_only_findings.length} other findings are internal-only (not used in outreach).
              </div>
            )}
          </details>
        )}

        {/* Slice 3.4 — Recipient (email channel only). Pre-filled with
            lead.email + discovered_emails; user can pick or type. */}
        {activeChannel === "email" && (sendStatus?.senders?.length > 0) && (
          <div className="flex items-center gap-2 text-xs" data-testid="email-from-row">
            <span className="text-zinc-500 w-14">From</span>
            <select
              value={fromSenderId}
              onChange={e => setFromSenderId(e.target.value)}
              className="flex-1 px-2 py-1.5 border border-zinc-200 rounded-md bg-white"
              data-testid="email-from-select"
            >
              <option value="">
                Default — {sendStatus?.default_sender_email || "workspace default"}
              </option>
              {sendStatus.senders.map(s => (
                <option key={s.id} value={s.id}>
                  {s.from_name ? `${s.from_name} <${s.from_email}>` : s.from_email}
                </option>
              ))}
            </select>
            <a
              href="/settings?tab=general#outreach-senders"
              target="_blank"
              rel="noreferrer"
              className="text-[10px] text-indigo-600 hover:underline whitespace-nowrap"
              data-testid="email-from-manage-link"
            >
              Manage →
            </a>
          </div>
        )}
        {activeChannel === "email" && (
          <div className="flex items-center gap-2 text-xs" data-testid="email-to-row">
            <span className="text-zinc-500 w-14">To</span>
            {candidateEmails.length > 1 && candidateEmails.includes(toEmail) ? (
              <select
                value={toEmail}
                onChange={e => setToEmail(e.target.value === "__custom__" ? "" : e.target.value)}
                className="flex-1 px-2 py-1.5 border border-zinc-200 rounded-md bg-white"
                data-testid="email-to-select"
              >
                {candidateEmails.map(em => <option key={em} value={em}>{em}</option>)}
                <option value="__custom__">Custom address…</option>
              </select>
            ) : candidateEmails.length > 1 ? (
              // User picked "Custom address…" — show a typeable input
              // PLUS a small "back to suggestions" link so they can
              // re-pick a crawler-discovered candidate without manually
              // retyping it.
              <>
                <input
                  type="email"
                  value={toEmail}
                  onChange={e => setToEmail(e.target.value)}
                  placeholder="recipient@example.com"
                  autoFocus
                  className="flex-1 px-2 py-1.5 border border-zinc-200 rounded-md bg-white"
                  data-testid="email-to-custom-input"
                />
                <button
                  type="button"
                  onClick={() => setToEmail(candidateEmails[0] || "")}
                  className="text-[10px] text-indigo-600 hover:underline whitespace-nowrap"
                  data-testid="email-to-back-to-suggestions"
                >
                  ↩ Pick from list
                </button>
              </>
            ) : (
              <input
                type="email"
                value={toEmail}
                onChange={e => setToEmail(e.target.value)}
                placeholder={candidateEmails.length === 0 ? "No email on record — enter one" : "recipient@example.com"}
                className="flex-1 px-2 py-1.5 border border-zinc-200 rounded-md bg-white"
                data-testid="email-to-input"
              />
            )}
            {sendStatus && (
              <span className="text-[10px] text-zinc-400 whitespace-nowrap" data-testid="send-cap-meta">
                {sendStatus.sent_today}/{sendStatus.daily_cap} sent today
              </span>
            )}
          </div>
        )}
        {activeChannel === "email" && isSuppressedRecipient && (
          <div className="text-[11px] text-rose-600 -mt-1 pl-16" data-testid="email-suppressed-warning">
            Unsubscribed on {isSuppressedRecipient.unsubscribed_at?.slice(0, 10)} ({isSuppressedRecipient.source}). Sending is disabled for this address.
          </div>
        )}
        {activeChannel === "email" && !isSuppressedRecipient && candidateEmails.length === 0 && !toEmail && (
          <div className="text-[11px] text-amber-600 -mt-1 pl-16" data-testid="email-none-warning">
            No email on record — enter one above or generate a draft to copy/paste manually.
          </div>
        )}

        {/* Slice 3.1 — Email template picker (email channel only) */}
        {activeChannel === "email" && (
          <div className="flex items-center gap-2 text-xs" data-testid="email-template-picker-row">
            <span className="text-zinc-500 w-14">Template</span>
            <select
              value={emailTemplateId}
              onChange={e => setEmailTemplateId(e.target.value)}
              className="flex-1 px-2 py-1.5 border border-zinc-200 rounded-md bg-white"
              data-testid="email-template-select"
            >
              <option value="">No template — use built-in draft</option>
              {emailTemplates.map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
            <a
              href="/email-templates?category=lead_intel_outreach"
              target="_blank"
              rel="noreferrer"
              className="text-[10px] text-indigo-600 hover:underline whitespace-nowrap"
              data-testid="email-template-manage-link"
            >
              Manage templates →
            </a>
          </div>
        )}

        {/* Inputs: sender / company / signature / voice / CTA */}
        <div className="grid grid-cols-2 gap-2 text-xs">
          <input type="text" placeholder="Sender name" value={defaults.sender_name}
                 onChange={e => setDefaults(d => ({...d, sender_name: e.target.value}))}
                 className="px-2 py-1 border border-zinc-200 rounded-md"
                 data-testid="outreach-sender-input"/>
          <input type="text" placeholder="Company" value={defaults.company_name}
                 onChange={e => setDefaults(d => ({...d, company_name: e.target.value}))}
                 className="px-2 py-1 border border-zinc-200 rounded-md"
                 data-testid="outreach-company-input"/>
          <input type="text" placeholder="Signature (e.g. hey@intake.com)" value={defaults.signature}
                 onChange={e => setDefaults(d => ({...d, signature: e.target.value}))}
                 className="px-2 py-1 border border-zinc-200 rounded-md col-span-2"
                 data-testid="outreach-signature-input"/>
          <input type="text" placeholder="Voice (e.g. warm, professional)" value={defaults.voice}
                 onChange={e => setDefaults(d => ({...d, voice: e.target.value}))}
                 className="px-2 py-1 border border-zinc-200 rounded-md"
                 data-testid="outreach-voice-input"/>
          <select value={ctaId} onChange={e => setCtaId(e.target.value)}
                  className="px-2 py-1 border border-zinc-200 rounded-md bg-white"
                  data-testid="outreach-cta-select">
            {ctaLibrary.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
        </div>

        {/* Action row */}
        <div className="flex gap-2 flex-wrap items-center">
          <Button size="sm" onClick={generate} disabled={generating}
                  data-testid="outreach-generate-btn"
                  className="bg-zinc-900 hover:bg-zinc-800 text-white">
            {generating ? "Generating…" : draft ? "Regenerate" : "Generate"}
          </Button>
          <Button size="sm" variant="outline" onClick={polish} disabled={!draft || polishing}
                  data-testid="outreach-polish-btn">
            {polishing ? "Polishing…" : "Polish with AI"}
          </Button>
          <Button size="sm" variant="outline" onClick={save} disabled={!draft || saving}
                  data-testid="outreach-save-btn">
            {saving ? "Saving…" : draft?.dirty ? "Save edits" : "Save"}
          </Button>
          <Button size="sm" variant="outline" onClick={copy} disabled={!draft}
                  data-testid="outreach-copy-btn">Copy</Button>
          {activeChannel === "email" && (
            <Button
              size="sm"
              onClick={() => setConfirmOpen(true)}
              disabled={!canSendEmail || sending}
              className="bg-zinc-900 hover:bg-zinc-800 text-white"
              data-testid="outreach-send-email-btn"
              title={
                isSuppressedRecipient ? "Recipient unsubscribed" :
                !toEmail ? "Add a recipient first" :
                (sendStatus && sendStatus.remaining <= 0) ? "Daily send cap reached" :
                "Send this draft to the recipient"
              }
            >
              {sending ? "Sending…" : "Send Email"}
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={saveDefaults} disabled={defaultsSaving}
                  data-testid="outreach-save-defaults-btn"
                  className="ml-auto text-[10px] text-zinc-500">
            {defaultsSaving ? "Saving…" : "Save as defaults"}
          </Button>
        </div>

        {/* Draft editor */}
        {!draft ? (
          <div className="text-[11px] text-zinc-400 italic px-2 py-4 text-center border border-dashed border-zinc-200 rounded-md">
            Pick a CTA and click Generate to create a {channel.label.toLowerCase()} draft.
          </div>
        ) : (
          <div data-testid={`outreach-draft-${activeChannel}`} className="space-y-2">
            {activeChannel === "email" && (
              <div className="flex gap-1 text-[10px]" data-testid="email-view-toggle">
                <button
                  type="button"
                  onClick={() => setEmailView("text")}
                  className={`flex items-center gap-1 px-2 py-1 rounded-md border ${emailView === "text" ? "bg-zinc-100 text-zinc-900 border-zinc-200" : "text-zinc-500 border-transparent hover:border-zinc-200"}`}
                  data-testid="email-view-text-btn"
                ><CodeIcon className="w-3 h-3"/> Plain text</button>
                <button
                  type="button"
                  onClick={() => setEmailView("preview")}
                  className={`flex items-center gap-1 px-2 py-1 rounded-md border ${emailView === "preview" ? "bg-zinc-100 text-zinc-900 border-zinc-200" : "text-zinc-500 border-transparent hover:border-zinc-200"}`}
                  data-testid="email-view-preview-btn"
                ><Eye className="w-3 h-3"/> Inbox preview</button>
              </div>
            )}

            {activeChannel === "email" && emailView === "preview" ? (
              <EmailPreview
                subject={draft.subject || ""}
                body={draft.body || ""}
                signature={draft.signature || ""}
                companyName={defaults.company_name}
                ctaLabel={ctaLibrary.find(c => c.id === ctaId)?.label || "Schedule"}
              />
            ) : (
              <>
                {(activeChannel === "email" || activeChannel === "contact_form") && (
                  <input type="text" placeholder="Subject" value={draft.subject || ""}
                         onChange={e => onFieldChange("subject", e.target.value)}
                         className="w-full text-sm font-medium px-2 py-1.5 border border-zinc-200 rounded-md"
                         data-testid="outreach-subject-input"/>
                )}
                <textarea value={draft.body || ""}
                          onChange={e => onFieldChange("body", e.target.value)}
                          rows={activeChannel === "ig_dm" ? 4 : 10}
                          className="w-full text-sm font-mono px-2 py-1.5 border border-zinc-200 rounded-md resize-y"
                          data-testid="outreach-body-textarea"/>
                {activeChannel === "email" && (
                  <textarea value={draft.signature || ""}
                            onChange={e => onFieldChange("signature", e.target.value)}
                            rows={3} placeholder="Signature"
                            className="w-full text-xs font-mono px-2 py-1.5 border border-zinc-200 rounded-md text-zinc-600"
                            data-testid="outreach-signature-textarea"/>
                )}
              </>
            )}

            <div className="flex items-center justify-between text-[10px] text-zinc-400">
              <span data-testid="outreach-char-count">{(draft.body || "").length} chars</span>
              <span>
                {draft.polished && <span className="text-violet-700 mr-2">✓ AI-polished</span>}
                {draft.dirty && <span className="text-amber-700">Unsaved changes</span>}
              </span>
            </div>
          </div>
        )}

        {/* Saved drafts pickup */}
        {savedDrafts.length > 0 && (
          <details className="text-[11px] border-t border-zinc-100 pt-2" data-testid="saved-drafts">
            <summary className="cursor-pointer text-zinc-600">
              Saved drafts <span className="text-zinc-400">({savedDrafts.length})</span>
            </summary>
            <ul className="mt-1.5 space-y-1">
              {savedDrafts.map(d => (
                <li key={d.id} className="flex items-center gap-2">
                  <span className="text-[9px] uppercase tracking-wider px-1 py-0.5 rounded border border-zinc-200 bg-zinc-50 text-zinc-600">{d.channel}</span>
                  <span className="text-zinc-700 truncate flex-1" title={d.subject || d.body}>{d.subject || (d.body || "").slice(0, 60)}</span>
                  <button type="button" onClick={() => {
                    setActiveChannel(d.channel);
                    setDrafts(prev => ({ ...prev, [d.channel]: { ...d, dirty: false } }));
                  }} className="text-[10px] text-zinc-500 hover:text-zinc-700"
                          data-testid={`load-saved-${d.id}`}>load</button>
                  <button type="button" onClick={async () => {
                    try {
                      await axios.delete(`${API}/lead-intel/leads/${leadId}/outreach/drafts/${d.id}`, { headers: tokenHeader() });
                      setSavedDrafts(prev => prev.filter(x => x.id !== d.id));
                      toast.success("Draft deleted");
                    } catch { toast.error("Delete failed"); }
                  }} className="text-[10px] text-rose-600 hover:text-rose-800"
                          data-testid={`delete-saved-${d.id}`}>delete</button>
                </li>
              ))}
            </ul>
          </details>
        )}
      </CardContent>

      {/* Slice 3.4 — Send confirmation modal */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent data-testid="send-confirm-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5 text-zinc-700" />
              Send outreach email
            </DialogTitle>
          </DialogHeader>
          {drafts.email && (
            <div className="text-sm text-zinc-700 space-y-3 leading-relaxed">
              <div className="rounded-lg bg-zinc-50 border border-zinc-200 p-3 space-y-2">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-zinc-400">From</div>
                  <div className="font-medium text-zinc-900" data-testid="confirm-from">
                    {(() => {
                      const picked = (sendStatus?.senders || []).find(s => s.id === fromSenderId);
                      if (picked) return picked.from_name ? `${picked.from_name} <${picked.from_email}>` : picked.from_email;
                      return sendStatus?.default_sender_email || "Workspace default";
                    })()}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-zinc-400">To</div>
                  <div className="font-medium text-zinc-900" data-testid="confirm-to">{toEmail}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-zinc-400">Subject</div>
                  <div className="font-medium text-zinc-900" data-testid="confirm-subject">{drafts.email.subject || "(no subject)"}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-zinc-400">First 200 characters</div>
                  <div
                    className="text-xs text-zinc-600 whitespace-pre-wrap font-mono leading-snug"
                    data-testid="confirm-body-preview"
                  >
                    {(drafts.email.body || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim().slice(0, 200)}
                    {(drafts.email.body || "").length > 200 ? "…" : ""}
                  </div>
                </div>
              </div>
              <p className="text-xs text-zinc-500">
                This will send via your workspace sender, log the activity, and mark the
                lead as <strong>Contacted</strong>. The email includes a 1-click unsubscribe
                link for compliance.
              </p>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setConfirmOpen(false)} disabled={sending} data-testid="confirm-cancel">
              Cancel
            </Button>
            <Button onClick={sendEmail} disabled={sending} className="bg-zinc-900 hover:bg-zinc-800" data-testid="confirm-send">
              {sending ? "Sending…" : "Send email"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}


// ───────────────────────── Email Inbox Preview ──────────────────────────
// Minimal premium SaaS aesthetic — Stripe / Notion / Linear / Attio vibe.
// Single column · ~600px · white bg · black text · black CTA · no gradients.

function EmailPreview({ subject, body, signature, companyName, ctaLabel }) {
  // Parse the body to extract a CTA-looking sentence at the end so we can
  // promote it to the black CTA button. We keep the rest of the body as
  // plain-text paragraphs.
  const { paragraphs, ctaText } = splitBodyForPreview(body);
  return (
    <div data-testid="email-inbox-preview" className="rounded-md border border-zinc-200 bg-zinc-50 p-4">
      <div className="text-[10px] text-zinc-400 uppercase tracking-wider mb-2">Inbox preview</div>
      <div className="mx-auto max-w-[600px] bg-white shadow-sm">
        {/* Header */}
        <div className="px-8 pt-10 pb-2">
          <div className="text-[15px] text-zinc-900 font-medium">{companyName || "HeyIntake"}</div>
        </div>
        {/* Subject */}
        <div className="px-8 pb-6">
          <div className="text-xl text-zinc-900 font-semibold leading-snug">{subject || "(no subject)"}</div>
        </div>
        {/* Body */}
        <div className="px-8 space-y-5 text-[14px] text-zinc-800 leading-relaxed">
          {paragraphs.map((p, i) => (
            <p key={`p-${i}`} style={{ whiteSpace: "pre-wrap" }}>{p}</p>
          ))}
        </div>
        {/* CTA button */}
        {ctaText && (
          <div className="px-8 pt-8 pb-2">
            <button
              type="button"
              className="inline-flex items-center justify-center px-5 py-2.5 bg-zinc-900 text-white text-sm font-medium rounded-md hover:bg-black"
            >
              {ctaLabel || "Schedule a call"}
            </button>
            <div className="text-[11px] text-zinc-400 mt-2 italic">CTA prompt: "{ctaText}"</div>
          </div>
        )}
        {/* Signature */}
        {signature && (
          <div className="px-8 pt-8 pb-12 text-[13px] text-zinc-600 leading-relaxed" style={{ whiteSpace: "pre-wrap" }}>
            {signature}
          </div>
        )}
      </div>
    </div>
  );
}


function splitBodyForPreview(body) {
  if (!body) return { paragraphs: [], ctaText: null };
  // Split into paragraphs by blank lines.
  const blocks = body.split(/\n\s*\n/).map(s => s.trim()).filter(Boolean);
  if (blocks.length === 0) return { paragraphs: [], ctaText: null };
  // Treat the LAST block as the CTA candidate ONLY if it looks like a
  // question / proposition (ends with "?" or contains "would you" / "open to").
  const last = blocks[blocks.length - 1];
  const isCta = /\?\s*$/.test(last) || /\b(would you|open to|interested|happy to share)\b/i.test(last);
  if (isCta) {
    return {
      paragraphs: blocks.slice(0, -1),
      ctaText: last,
    };
  }
  return { paragraphs: blocks, ctaText: null };
}
